import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, TriangleAlert } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import { isLocalAbsolutePath } from './terminalDefaultCwd';
import { useAgentCwdPrompt, answerAgentCwd } from './agentCwdPrompt';
import './TerminalProfiles.css';

async function pickFolder() {
  try {
    const { open } = await import('@tauri-apps/plugin-dialog');
    const picked = await open({ directory: true, multiple: false });
    return typeof picked === 'string' ? picked : null;
  } catch {
    return null;
  }
}

/**
 * Working-directory prompt for an agent opening in a new tab while no
 * Terminal default folder is set (see agentCwdPrompt.js). "Use as default
 * folder" (on by default) saves it to Terminal settings, so it is asked once.
 */
export default function AgentCwdModal() {
  const { t } = useTranslation();
  const request = useAgentCwdPrompt((s) => s.request);
  const setDefaultCwd = useTerminalConfigStore((s) => s.setDefaultCwd);
  const [path, setPath] = useState('');
  const [remember, setRemember] = useState(true);

  useEffect(() => {
    if (request) {
      setPath(request.suggested || '');
      // A one-off "open in another folder" must not replace a saved default.
      setRemember(!request.hasDefault);
    }
  }, [request]);

  if (!request) return null;

  const value = path.trim();
  const valid = isLocalAbsolutePath(value);
  const submit = () => {
    if (!valid) return;
    if (remember) setDefaultCwd(value);
    answerAgentCwd(value);
  };

  return (
    <Modal
      isOpen
      onClose={() => answerAgentCwd(null)}
      title={t('terminalExt.agentCwdTitle', 'Choose a working directory')}
      subtitle={request.profileName}
    >
      <form
        className="term-profile-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <p className="settings-row-desc">
          {t(
            'terminalExt.agentCwdDesc',
            'The agent works in this folder. Pick your project folder — starting in your home folder makes the system ask for access to Desktop, Documents, Downloads and more.'
          )}
        </p>
        <div className="term-field-inline">
          <input
            autoFocus
            type="text"
            className="settings-text-input flex-1 min-w-0 term-mono"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder={t('terminalExt.agentCwdPlaceholder', 'Project folder')}
            aria-label={t('terminalExt.profileCwd', 'Working directory')}
            spellCheck={false}
            autoComplete="off"
          />
          <button
            type="button"
            className="term-field-btn"
            onClick={async () => {
              const p = await pickFolder();
              if (p) setPath(p);
            }}
          >
            <FolderOpen size={13} aria-hidden />
            {t('terminalExt.browse', 'Browse…')}
          </button>
        </div>
        {value && !valid && (
          <div className="term-field-warn" role="alert">
            <TriangleAlert size={12} aria-hidden />
            {t('terminalExt.agentCwdInvalid', 'Not a local folder path (like C:\\dev or /Users/me/dev).')}
          </div>
        )}
        <label className="term-check">
          <input type="checkbox" className="checkbox-brand" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          <span>
            {t('terminalExt.agentCwdRemember', 'Use as default folder for new terminals')}
            <span className="term-check-sub">{t('terminalExt.agentCwdRememberSub', 'Change it any time in Terminal settings')}</span>
          </span>
        </label>
        <div className="modal-footer-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={() => answerAgentCwd(null)}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            disabled={!valid}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
          >
            {t('terminalExt.agentCwdOpen', 'Open')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
